import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import classnames from "classnames"
import {
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap"

import DataCard from "components/Common/DataCard"
import PageWrapper from "components/Common/PageWrapper"
import StatusBadge from "components/Common/StatusBadge"
import { ACCESS } from "constants/accessPermissions"
import { prepareDataWithLoading } from "helpers/loading_helper"
import { useAuth } from "hooks/authHook"
import { useListParams } from "hooks/useListParams"
import { useShowroomBySlug } from "hooks/showroomHook"
import { formatReadableTime } from "utils/formatDate"
import ShowroomContactUpdate from "./ShowroomContactUpdate"
import ShowroomDetailsUpdate from "./ShowroomDetailsUpdate"
import ShowroomProfileCard from "./ShowroomProfileCard"
import ShowroomProfileUpdate from "./ShowroomProfileUpdate"
import ShowroomProprietorUpdate from "./ShowroomProprietorUpdate"
import ShowroomSocialUpdate from "./ShowroomSocialUpdate"
import ShowroomStocksTab from "./ShowroomStocksTab"

const renderLink = (url, label) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {label || url}
    </a>
  ) : (
    "-"
  )

const ShowroomProfile = () => {
  const { slug } = useParams()
  const { data, isLoading } = useShowroomBySlug(slug)
  const showroom = data?.data || null

  const { hasAnyPermission } = useAuth()
  const canViewStocks = hasAnyPermission([ACCESS.STOCK.VIEW_STOCK_LIST])
  const canEditShowroom = hasAnyPermission([ACCESS.SHOWROOM.EDIT])

  const { params, updateParams } = useListParams({ tab: "overview" })
  const [activeTab, setActiveTab] = useState(params?.tab || "overview")

  document.title = `Showroom Profile${
    showroom?.name ? ` | ${showroom.name}` : ""
  }`

  const toggleTab = tab => {
    updateParams({ tab })
    if (activeTab !== tab) setActiveTab(tab)
  }

  useEffect(() => {
    if (params?.tab && params.tab !== activeTab) setActiveTab(params.tab)
  }, [params?.tab])

  useEffect(() => {
    if (activeTab === "stocks" && !canViewStocks) {
      setActiveTab("overview")
      updateParams({ tab: "overview" })
    }
  }, [activeTab, canViewStocks])

  const contactInfo = prepareDataWithLoading(
    [
      { label: "Mobile", value: showroom?.mobile },
      { label: "Alt Mobile", value: showroom?.alt_mobile },
      { label: "Email", value: showroom?.email },
      { label: "Website", value: renderLink(showroom?.website) },
      { label: "Stock URL", value: renderLink(showroom?.stock_url, "View Stock") },
      { label: "Google Map", value: renderLink(showroom?.google_map, "View Map") },
    ],
    isLoading
  )

  const socialInfo = prepareDataWithLoading(
    [
      { label: "Facebook", value: showroom?.facebook },
      { label: "Instagram", value: showroom?.instagram },
      { label: "YouTube", value: showroom?.youtube },
      { label: "TikTok", value: showroom?.tiktok },
      { label: "LinkedIn", value: showroom?.linkedin },
    ],
    isLoading
  )

  const detailsInfo = prepareDataWithLoading(
    [
      {
        label: "Capacity",
        value: showroom?.capacity ? `${showroom.capacity} cars` : "-",
      },
      {
        label: "Service Center",
        value: (
          <StatusBadge
            value={showroom?.has_service_center}
            trueLabel="Yes"
            falseLabel="No"
          />
        ),
      },
      {
        label: "Parking Available",
        value: (
          <StatusBadge
            value={showroom?.has_parking}
            trueLabel="Yes"
            falseLabel="No"
          />
        ),
      },
      {
        label: "Opening Time",
        value: showroom?.opening_time
          ? formatReadableTime(showroom.opening_time)
          : "-",
      },
      {
        label: "Closing Time",
        value: showroom?.closing_time
          ? formatReadableTime(showroom.closing_time)
          : "-",
      },
      { label: "Latitude", value: showroom?.latitude },
      { label: "Longitude", value: showroom?.longitude },
      { label: "Description", value: showroom?.description },
      {
        label: "Status",
        value: (
          <StatusBadge
            value={showroom?.is_active}
            trueLabel="Active"
            falseLabel="Inactive"
          />
        ),
      },
    ],
    isLoading
  )

  const proprietorInfo = prepareDataWithLoading(
    [
      { label: "Proprietor Name", value: showroom?.proprietor_name },
      { label: "Proprietor Mobile", value: showroom?.proprietor_mobile },
      { label: "Proprietor Address", value: showroom?.proprietor_address },
    ],
    isLoading
  )

  const tabs = [
    { id: "overview", label: "Overview", icon: "bx bx-store" },
    ...(canViewStocks
      ? [{ id: "stocks", label: "Vehicle Stocks", icon: "bx bx-car" }]
      : []),
    { id: "contact", label: "Contact", icon: "bx bx-phone" },
    { id: "social", label: "Social", icon: "bx bx-share-alt" },
  ]

  return (
    <PageWrapper
      title="Showroom Profile"
      breadcrumbItem={showroom?.name || slug || "Profile"}
      cardBody={false}
    >
      <Row>
        <Col lg="4" className="mb-4">
          <ShowroomProfileCard
            loading={isLoading}
            data={showroom}
            onShowroom={
              showroom && canEditShowroom ? (
                <ShowroomProfileUpdate showroom={showroom} />
              ) : null
            }
          />
        </Col>

        <Col lg="8">
          <Nav pills className="nav-pills-custom mb-4">
            {tabs.map(tab => (
              <NavItem key={tab.id}>
                <NavLink
                  className={classnames({ active: activeTab === tab.id })}
                  onClick={() => toggleTab(tab.id)}
                >
                  <i className={`${tab.icon} me-2`}></i>
                  {tab.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="overview">
              <Row>
                <Col md="12" className="mb-4">
                  <DataCard
                    title="Showroom Details"
                    icon="bx bx-store"
                    data={detailsInfo}
                    action={
                      showroom && canEditShowroom ? (
                        <ShowroomDetailsUpdate showroom={showroom} />
                      ) : null
                    }
                  />
                </Col>
                <Col md="12" className="mb-4">
                  <DataCard
                    title="Proprietor Details"
                    icon="bx bx-user-pin"
                    data={proprietorInfo}
                    action={
                      showroom && canEditShowroom ? (
                        <ShowroomProprietorUpdate showroom={showroom} />
                      ) : null
                    }
                  />
                </Col>
              </Row>
            </TabPane>

            {canViewStocks ? (
              <TabPane tabId="stocks">
                {activeTab === "stocks" ? (
                  <ShowroomStocksTab showroomId={showroom?.id} />
                ) : null}
              </TabPane>
            ) : null}

            <TabPane tabId="contact">
              <Row>
                <Col md="12" className="mb-4">
                  <DataCard
                    title="Contact Information"
                    icon="bx bx-phone"
                    data={contactInfo}
                    action={
                      showroom && canEditShowroom ? (
                        <ShowroomContactUpdate showroom={showroom} />
                      ) : null
                    }
                  />
                </Col>
              </Row>
            </TabPane>

            <TabPane tabId="social">
              <Row>
                <Col md="12" className="mb-4">
                  <DataCard
                    title="Social Links"
                    icon="bx bx-share-alt"
                    data={socialInfo}
                    action={
                      showroom && canEditShowroom ? (
                        <ShowroomSocialUpdate showroom={showroom} />
                      ) : null
                    }
                  />
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default ShowroomProfile
